import { useEffect, useState } from 'react';
import { Button, Flex, Modal, TextInput } from '@mantine/core';
import {
	useTranslationCommonForm,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { isValidEmail } from '@dealstack/shared/lib/validation';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';

interface Props {
	isOpen: boolean;
	isLoading: boolean;
	initialEmail?: string;
	initialFirstName?: string;
	initialLastName?: string;
	onClose: () => void;
	onSubmit: (email: string, firstName: string, lastName: string) => void;
}

export const AuthorisedSignatoryModal = ({
	isOpen,
	isLoading,
	initialEmail,
	initialFirstName,
	initialLastName,
	onClose,
	onSubmit,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const tCommonForm = useTranslationCommonForm();
	const [email, setEmail] = useState(initialEmail ?? '');
	const [firstName, setFirstName] = useState(initialFirstName ?? '');
	const [lastName, setLastName] = useState(initialLastName ?? '');
	const [showError, setShowError] = useState(false);

	useEffect(() => {
		if (isOpen) {
			setEmail(initialEmail ?? '');
			setFirstName(initialFirstName ?? '');
			setLastName(initialLastName ?? '');
			setShowError(false);
		}
	}, [isOpen, initialEmail, initialFirstName, initialLastName]);

	const trimmedEmail = email.trim();
	const trimmedFirstName = firstName.trim();
	const trimmedLastName = lastName.trim();
	const emailValid = isValidEmail(trimmedEmail);
	const firstNameValid = trimmedFirstName.length > 0;
	const lastNameValid = trimmedLastName.length > 0;
	const formValid = emailValid && firstNameValid && lastNameValid;

	const handleSubmit = () => {
		if (!formValid) {
			setShowError(true);
			return;
		}
		onSubmit(trimmedEmail, trimmedFirstName, trimmedLastName);
	};

	const handleClose = () => {
		setEmail('');
		setFirstName('');
		setLastName('');
		setShowError(false);
		onClose();
	};

	return (
		<Modal
			opened={isOpen}
			onClose={handleClose}
			title={tMipManage('documents_page.authorised_signatory.modal_title')}
			centered
		>
			<Flex direction="column" gap="md" p="md">
				<TextInput
					data-testid={TEST_ELEMENT_IDS.AUTHORISED_SIGNATORY_MODAL_EMAIL_INPUT}
					label={tMipManage('contracts.modal.e_sign.email')}
					placeholder={tMipManage(
						'documents_page.authorised_signatory.email_placeholder'
					)}
					type="email"
					required
					value={email}
					error={showError && !emailValid ? tCommonForm('invalid_email') : undefined}
					onChange={(e) => {
						setEmail(e.currentTarget.value);
						if (showError) {
							setShowError(false);
						}
					}}
					onBlur={() => {
						if (trimmedEmail.length > 0 && !emailValid) {
							setShowError(true);
						}
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							handleSubmit();
						}
					}}
				/>
				<TextInput
					data-testid={TEST_ELEMENT_IDS.AUTHORISED_SIGNATORY_MODAL_FIRST_NAME_INPUT}
					label={tMipManage('contracts.modal.e_sign.first_name')}
					placeholder={tMipManage(
						'documents_page.authorised_signatory.first_name_placeholder'
					)}
					required
					value={firstName}
					error={showError && !firstNameValid ? tCommonForm('field_required') : undefined}
					onChange={(e) => {
						setFirstName(e.currentTarget.value);
						if (showError) {
							setShowError(false);
						}
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							handleSubmit();
						}
					}}
				/>
				<TextInput
					data-testid={TEST_ELEMENT_IDS.AUTHORISED_SIGNATORY_MODAL_LAST_NAME_INPUT}
					label={tMipManage('contracts.modal.e_sign.last_name')}
					placeholder={tMipManage(
						'documents_page.authorised_signatory.last_name_placeholder'
					)}
					required
					value={lastName}
					error={showError && !lastNameValid ? tCommonForm('field_required') : undefined}
					onChange={(e) => {
						setLastName(e.currentTarget.value);
						if (showError) {
							setShowError(false);
						}
					}}
					onKeyDown={(e) => {
						if (e.key === 'Enter') {
							handleSubmit();
						}
					}}
				/>
				<Flex gap="sm" justify="flex-end">
					<Button variant="subtle" onClick={handleClose} disabled={isLoading}>
						{tMipManage('documents_page.witness.cancel')}
					</Button>
					<Button
						data-testid={TEST_ELEMENT_IDS.AUTHORISED_SIGNATORY_MODAL_SUBMIT_BUTTON}
						variant="filled"
						disabled={!formValid || isLoading}
						loading={isLoading}
						onClick={handleSubmit}
					>
						{tMipManage('documents_page.witness.send')}
					</Button>
				</Flex>
			</Flex>
		</Modal>
	);
};
